"use client"

import { useState } from "react"
import { useForm, FormProvider } from "react-hook-form"
import { SendLink } from "@/features/homeowners/actions/home-owner.actions"
import { Button } from "@/shared/components/ui/button"
import {
  SUPPORTER_ROUTES,
  DASHBOARD_ROUTE,
  AUTH_PAGE_ROUTE,
} from "@/shared/constants/pages-routes"
import AuthBgWrapper from "@/features/auth/components/auth-bg-wrapper"
import AuthCardWrapper from "@/features/auth/components/auth-card-wrapper"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import { loginScreenBgImage } from "@/features/auth/constants/image"
import axios from "axios"
import successToast from "@/shared/lib/toast/success.toast"
import errorToast from "@/shared/lib/toast/error.toast"
import Link from "next/link"

export default function RegisterIntro() {
  const [isChecked, setIsChecked] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const methods = useForm()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked)
  }

  const handleRegister = async () => {
    const email = methods.getValues("email")
    const role = "Supporter"

    if (!email) {
      errorToast("Please enter your email")
      return
    }

    setIsLoading(true)
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_BASE_BACKEND_URL}/auth/registration/register`,
        { email, role },
        { headers: { "Content-Type": "application/json" } }
      )
      console.log(response,"response")
    
      if (response) {
        const res = await SendLink(email)
        if (res?.success) {
          successToast("Link shared successfully on your email")
        }
      }
    } catch (error: any) {
      if(error.status==404){
        errorToast('A user with this email already exists')
      }else{
        errorToast(error.message)
      }
      
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthBgWrapper image={loginScreenBgImage}>
      <AuthCardWrapper
        headerTitle="Welcome!"
        headerDescription="We're so glad you're here. Your support fuels real relief and restores hope after disaster."
        
      >
        <div className="space-y-4 md:space-y-6 w-full">
          {/* Intro text */}
          <div className="text-center space-y-2">
            <p className="text-gray-800 text-sm md:text-base">
              Input your email below and check out the latest status where hope is
              being restored!
            </p>
          </div>

          {/* Form */}
          <FormProvider {...methods}>
            <form className="flex flex-col gap-4 md:gap-6 w-full max-w-sm mx-auto">
              <FormTextInput
                inputGroupProps={{ label: "Email Address" }}
                type="email"
                placeholder="Enter your email"
                name="email"
                control={methods.control}
              />

              <label className="flex items-center gap-3 text-sm md:text-base text-gray-700 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleChange}
                    className="w-5 h-5 md:w-4 md:h-4 border-gray-300 focus:ring focus:ring-blue-400 opacity-0 absolute"
                  />
                  <div
                    className={`w-5 h-5 md:w-4 md:h-4 border-2 border-gray-300 flex items-center justify-center ${
                      isChecked ? "bg-gray-100" : "bg-white"
                    }`}
                  >
                    {isChecked && (
                      <svg
                        className="w-3 h-3 text-gray-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <span>I agree to receive updates</span>
              </label>
            </form>
          </FormProvider>

          {/* Dashboard button */}
          <Button
            asChild
            className="w-full whitespace-normal break-words text-center"
          >
            <Link href={DASHBOARD_ROUTE.dashboard}>
              Click here to check out the latest status where hope is being
              restored!
            </Link>
          </Button>

          {/* Register button */}
          <Button
            onClick={handleRegister}
            disabled={isLoading}
            className="w-full h-15 whitespace-normal break-words text-center"
          >
            {isLoading
              ? "Registering..."
              : "Ready to go deeper? Register for an account here to follow the mission and see your impact in real time!"}
          </Button>

          {/* Footer link */}
          <p className="text-sm md:text-base text-center text-gray-800">
            {`Already have an account? `}
            <Link href={AUTH_PAGE_ROUTE.login} className="text-primary underline">
              Sign in
            </Link>
          </p>
        </div>
      </AuthCardWrapper>
    </AuthBgWrapper>
  )
}
